import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Home,
  Plus,
  RefreshCw,
  ShieldX,
  Users,
} from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { ApiError, type OnboardedTeam } from '../../api';
import { Logo } from '../../app/Logo';
import { Badge, Button, Spinner } from '../../ui';
import { useActiveTeam } from './useActiveTeam';
import { BUILT_IN_TEAM } from './builtInTeam';

/** Per-card access-check state for an onboarded team. */
interface AccessState {
  checking: boolean;
  /** Set when access was denied; carries the human-readable reason. */
  deniedReason?: string;
  /** Set when the access check itself failed (network/backend). */
  error?: string;
}

function errMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return fallback;
}

/**
 * The tenant entry gate: "Choose your team". Shown to a signed-in user who has
 * not yet chosen an active team. Offers three paths:
 *  1. The built-in "current workspace" (bundleKey '') — enters directly.
 *  2. Any already-onboarded team — clicking runs CheckTeamAccess and enters
 *     when authorized, or shows the denial reason inline.
 *  3. "Onboard a new team" — routes to the /onboarding wizard.
 */
export function ChooseTeamScreen() {
  const api = useApi();
  const navigate = useNavigate();
  const { setActiveTeam } = useActiveTeam();

  const [teams, setTeams] = useState<OnboardedTeam[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [access, setAccess] = useState<Record<string, AccessState>>({});

  const loadTeams = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const list = await api.teamOnboarding.listOnboardedTeams();
      setTeams(list);
    } catch (e) {
      setTeams(null);
      setLoadError(errMessage(e, 'Failed to load onboarded teams.'));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void loadTeams();
  }, [loadTeams]);

  const enterBuiltIn = useCallback(() => {
    setActiveTeam(BUILT_IN_TEAM);
  }, [setActiveTeam]);

  const chooseTeam = useCallback(
    async (team: OnboardedTeam) => {
      setAccess((prev) => ({
        ...prev,
        [team.bindleId]: { checking: true },
      }));
      try {
        const res = await api.teamOnboarding.checkTeamAccess({
          bindleId: team.bindleId,
        });
        if (res.authorized) {
          // Onboarded teams isolate their data under bundleKey = bindleId.
          setActiveTeam({
            bindleId: team.bindleId,
            teamName: team.teamName || team.bindleId,
            bundleKey: team.bindleId,
          });
          return;
        }
        setAccess((prev) => ({
          ...prev,
          [team.bindleId]: {
            checking: false,
            deniedReason:
              res.reason ?? 'You are not authorized to access this team.',
          },
        }));
      } catch (e) {
        setAccess((prev) => ({
          ...prev,
          [team.bindleId]: {
            checking: false,
            error: errMessage(e, 'Access check failed.'),
          },
        }));
      }
    },
    [api, setActiveTeam],
  );

  return (
    <section
      aria-labelledby="tenant-gate-heading"
      className="animate-fade-in mx-auto max-w-3xl py-10"
    >
      <div className="flex flex-col items-center text-center">
        <Logo size="lg" />
        <h1
          id="tenant-gate-heading"
          className="mt-6 text-2xl font-semibold tracking-tight text-zinc-50"
        >
          Choose your team
        </h1>
        <p className="mt-2 max-w-md text-sm text-zinc-400">
          Vector is multi-tenant. Pick the workspace you want to enter — your
          data is scoped to the team you choose.
        </p>
      </div>

      <div className="mt-8 space-y-6">
        {/* Built-in current workspace */}
        <div>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
            Current workspace
          </h2>
          <button
            type="button"
            onClick={enterBuiltIn}
            className="group flex w-full items-center gap-4 rounded-2xl border border-surface-border bg-surface-1 p-5 text-left transition-colors hover:border-accent/60 hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent-fg">
              <Home className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-zinc-100">
                {BUILT_IN_TEAM.teamName}
                <span className="ml-2 text-xs font-normal text-zinc-500">
                  current workspace
                </span>
              </span>
              <span className="mt-0.5 block text-xs text-zinc-500">
                Enter the built-in workspace with your existing data.
              </span>
            </span>
            <Badge tone="neutral">DEFAULT</Badge>
          </button>
        </div>

        {/* Onboarded teams */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
              Onboarded teams
            </h2>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<RefreshCw className="h-4 w-4" />}
              onClick={() => void loadTeams()}
              loading={loading}
              aria-label="Refresh onboarded teams"
            >
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center rounded-2xl border border-surface-border bg-surface-1 py-10">
              <Spinner label="Loading teams" />
            </div>
          ) : loadError ? (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-5">
              <p className="text-sm text-red-400" role="alert">
                {loadError}
              </p>
              <Button
                className="mt-3"
                variant="secondary"
                size="sm"
                leftIcon={<RefreshCw className="h-4 w-4" />}
                onClick={() => void loadTeams()}
              >
                Try again
              </Button>
            </div>
          ) : teams && teams.length > 0 ? (
            <ul className="space-y-3">
              {teams.map((team) => {
                const state = access[team.bindleId];
                const denied = state?.deniedReason ?? state?.error;
                return (
                  <li key={team.bindleId}>
                    <button
                      type="button"
                      onClick={() => void chooseTeam(team)}
                      disabled={state?.checking}
                      aria-busy={state?.checking}
                      className="group flex w-full items-center gap-4 rounded-2xl border border-surface-border bg-surface-1 p-5 text-left transition-colors hover:border-accent/60 hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-wait disabled:opacity-70"
                    >
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-surface-3 text-accent">
                        <Building2 className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-zinc-100">
                          {team.teamName || team.bindleId}
                        </span>
                        <span className="mt-0.5 block truncate font-mono text-xs text-zinc-500">
                          {team.bindleId}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        {typeof team.accountCount === 'number' && (
                          <span className="hidden items-center gap-1 text-xs text-zinc-500 sm:inline-flex">
                            <Users className="h-3.5 w-3.5" aria-hidden="true" />
                            {team.accountCount}
                          </span>
                        )}
                        {team.status && (
                          <Badge tone="info">{team.status}</Badge>
                        )}
                        {state?.checking && <Spinner />}
                      </span>
                    </button>
                    {denied && (
                      <p
                        className="mt-1.5 flex items-start gap-1.5 px-1 text-xs text-red-400"
                        role="alert"
                      >
                        <ShieldX
                          className="mt-0.5 h-3.5 w-3.5 shrink-0"
                          aria-hidden="true"
                        />
                        <span>
                          <span className="font-medium">Not authorized</span>
                          {` — ${denied}`}
                        </span>
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="rounded-2xl border border-dashed border-surface-border bg-surface-1/60 p-8 text-center">
              <p className="text-sm text-zinc-400">
                No teams have been onboarded yet.
              </p>
              <p className="mt-1 text-xs text-zinc-500">
                Onboard a team to isolate its data under its own tenant bundle.
              </p>
            </div>
          )}
        </div>

        {/* Onboard a new team */}
        <div className="flex justify-center pt-2">
          <Button
            variant="secondary"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => navigate('/onboarding')}
          >
            Onboard a new team
          </Button>
        </div>
      </div>
    </section>
  );
}

export default ChooseTeamScreen;
